import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const destinations = [
    {
        id: 1,
        name: "Lac Rose",
        region: "Dakar",
        description: "Le célèbre lac aux eaux roses, unique au monde",
        image: "/images/lac-rose.png",
        category: "Nature"
    },
    {
        id: 2,
        name: "Île de Gorée",
        region: "Dakar",
        description: "Site historique classé au patrimoine mondial de l'UNESCO",
        image: "/images/goree-island.jpg",
        category: "Histoire"
    },
    {
        id: 3,
        name: "Saly Portudal",
        region: "Thiès",
        description: "Station balnéaire prisée avec plages de sable fin",
        image: "/images/saly-beach.jpg",
        category: "Plage"
    },
    {
        id: 4,
        name: "Aéroport AIBD",
        region: "Diass",
        description: "Aéroport international Blaise Diagne",
        image: "/images/dakar-airport-real.jpg",
        category: "Transport"
    },
    {
        id: 5,
        name: "Saint-Louis",
        region: "Saint-Louis",
        description: "Ancienne capitale coloniale, ville d'art et d'histoire",
        image: "/images/saint-louis.jpg",
        category: "Histoire"
    },
    {
        id: 6,
        name: "Thiès",
        region: "Thiès",
        description: "Deuxième ville du Sénégal, carrefour économique",
        image: "/images/thies-city.jpg",
        category: "Ville"
    },
    {
        id: 7,
        name: "Mbour",
        region: "Thiès",
        description: "Ville côtière connue pour son marché aux poissons",
        image: "/images/mbour-fishing.jpg",
        category: "Plage"
    },
    {
        id: 8,
        name: "La Somone",
        region: "Thiès",
        description: "Lagune paisible idéale pour l'écotourisme",
        image: "/images/somone-lagoon.jpg",
        category: "Nature"
    },
    {
        id: 9,
        name: "Casamance",
        region: "Ziguinchor",
        description: "Région verdoyante aux paysages tropicaux",
        image: "/images/casamance.jpg",
        category: "Nature"
    },
    {
        id: 10,
        name: "Touba",
        region: "Diourbel",
        description: "Ville sainte et grande mosquée emblématique",
        image: "/images/touba-mosque.jpg",
        category: "Culture"
    },
    {
        id: 11,
        name: "Dakar Centre",
        region: "Dakar",
        description: "Capitale vibrante, mélange de modernité et tradition",
        image: "/images/dakar-skyline.png",
        category: "Ville"
    },
    {
        id: 12,
        name: "Popenguine",
        region: "Thiès",
        description: "Village paisible avec réserve naturelle",
        image: "/images/popenguine.jpg",
        category: "Nature"
    }
];

const categories = ["Tous", "Nature", "Plage", "Ville", "Histoire", "Culture", "Transport"];

const DestinationsPage = () => {
    const [activeCategory, setActiveCategory] = useState("Tous");
    const [hoveredId, setHoveredId] = useState(null);

    const filteredDestinations = activeCategory === "Tous"
        ? destinations
        : destinations.filter(d => d.category === activeCategory);

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            {/* Hero Section */}
            <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/images/dakar-hero.png"
                        alt="Sénégal"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-gray-50" />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 text-center">
                    <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm mb-6">
                        🇸🇳 Découvrez le Sénégal
                    </span>
                    <h1
                        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                        Nos Destinations
                    </h1>
                    <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
                        Explorez les plus beaux lieux du Sénégal avec notre service de transport VTC premium
                    </p>
                </div>
            </section>

            {/* Category Filter */}
            <section className="sticky top-16 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 py-4">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${activeCategory === category
                                    ? 'bg-gray-900 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Destinations Grid */}
            <section className="py-12 sm:py-20">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredDestinations.map((destination, index) => (
                            <div
                                key={destination.id}
                                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                                onMouseEnter={() => setHoveredId(destination.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                {/* Image Container */}
                                <div className="relative h-64 overflow-hidden">
                                    <img
                                        src={destination.image}
                                        alt={destination.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                                    {/* Category Badge */}
                                    <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-800">
                                        {destination.category}
                                    </span>

                                    {/* Hover Overlay */}
                                    <div className={`absolute inset-0 bg-gray-900/40 flex items-center justify-center transition-opacity duration-300 ${hoveredId === destination.id ? 'opacity-100' : 'opacity-0'
                                        }`}>
                                        <Link
                                            to="/contact"
                                            className="px-6 py-3 bg-white text-gray-900 rounded-full font-medium flex items-center gap-2 transform transition-transform hover:scale-105"
                                        >
                                            Réserver <ArrowRight size={16} />
                                        </Link>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                                            {destination.name}
                                        </h3>
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-500 text-sm mb-3">
                                        <MapPin size={14} />
                                        <span>{destination.region}</span>
                                    </div>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {destination.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 sm:py-24 bg-gray-900">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center">
                    <h2
                        className="text-2xl sm:text-3xl md:text-4xl text-white mb-6"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                        Prêt à explorer le Sénégal ?
                    </h2>
                    <p className="text-white/80 mb-8 max-w-xl mx-auto">
                        Réservez votre transfert avec Barham Driver pour un voyage confortable vers toutes ces destinations
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
                    >
                        Demander un devis <ArrowRight size={18} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default DestinationsPage;
